// src/client/scenes/optimizedGantt/graph/elementsOverlay/element/utils/drag.ts
//
// Drop-in replacement — the returned API ({ startDrag }) is unchanged, so
// Element call sites need no edits. `events` may keep being an inline object
// at the call site: handlers read it through a latest-ref, so its identity
// no longer matters.
//
// Before: every Element instance held 4 permanent window listeners
// (4 × ~300 = ~1,200 at rest), re-subscribed on EVERY render (dep-less
// effect → ~2,400 listener ops per overlay pass), and ran an unconditional
// getBoundingClientRect on every mousemove anywhere — ~300 forced layout
// reads per move — because the hasSelection() gate came AFTER the rect read.
//
// After: zero window listeners at rest; exactly 4 during an active drag,
// owned by the dragged element; one gBCR + one edit per move, on that
// element only; zero listener churn on renders; zero renders from the hook
// itself (the old setCanMove caused 2 per drag).

import React from "react";
import { Identifiable, Project } from "@planzone/dto";

type DragEvents = {
  edit: (data: number) => void;
  stopDrag: () => void;
};

// The old `!!mouseEvent.clientX` check misclassified a genuine mouse event
// at clientX === 0 (left viewport edge) as touch, then read
// `changedTouches` off a MouseEvent → TypeError. `in` narrowing is exact.
function getClientX(e: MouseEvent | TouchEvent): number {
  if ("changedTouches" in e) {
    return e.changedTouches.length > 0 ? e.changedTouches[0].clientX : 0;
  }
  return e.clientX;
}

function useElementDrag(
  graphRef: React.RefObject<HTMLDivElement | null>,
  project: Identifiable<Project> | null,
  events: DragEvents,
) {
  // A drag outlives many parent renders; handlers must call the LATEST
  // edit/stopDrag. Reading through a ref removes any dependency on the
  // callbacks' identity — this is what made the dep-less effect "necessary"
  // in the old version.
  const eventsRef = React.useRef(events);
  React.useLayoutEffect(() => {
    eventsRef.current = events;
  });

  // Replaces the old `canMove` React state: it was only ever read inside
  // event handlers, never rendered. Non-null ⇔ a drag is in progress, and
  // it doubles as the detach function.
  const teardownRef = React.useRef<(() => void) | null>(null);

  const getNextMoveValue = (graphElement: HTMLDivElement, offset: number) => {
    // Read per move rather than cached at drag start: the pane can scroll
    // mid-drag (wheel still fires while the button is held), which moves
    // the graph's viewport-relative left. One rect read per move on a
    // single element is ~1 layout read per frame — negligible.
    const rect = graphElement.getBoundingClientRect();
    return Math.round(offset - rect.left);
  };

  const startDrag = (initialOffset: number = 0) => {
    if (teardownRef.current) return; // already dragging

    if (graphRef.current) {
      eventsRef.current.edit(getNextMoveValue(graphRef.current, initialOffset));
    }

    const dispatchChange = (e: MouseEvent | TouchEvent) => {
      if (!graphRef.current) return;
      eventsRef.current.edit(getNextMoveValue(graphRef.current, getClientX(e)));
    };

    const teardown = () => {
      window.removeEventListener("touchmove", dispatchChange);
      window.removeEventListener("mousemove", dispatchChange);
      window.removeEventListener("mouseup", stop);
      window.removeEventListener("touchend", stop);
      teardownRef.current = null;
    };

    const stop = () => {
      // Teardown first: on touch devices the browser fires touchend and
      // then a synthetic mouseup — removing both listeners synchronously
      // guarantees stopDrag runs exactly once.
      teardown();
      eventsRef.current.stopDrag();
    };

    window.addEventListener("touchmove", dispatchChange);
    window.addEventListener("mousemove", dispatchChange);
    window.addEventListener("mouseup", stop);
    window.addEventListener("touchend", stop);
    teardownRef.current = teardown;
  };

  // If the element unmounts mid-drag (e.g. a data refresh removes the row),
  // detach the window listeners it owned.
  React.useEffect(() => {
    return () => {
      if (teardownRef.current) teardownRef.current();
    };
  }, []);

  return { startDrag };
}

export { useElementDrag };
